import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { get } from '../utils/http'
import { clean } from '../utils/string'
import { useQuery } from '@tanstack/react-query'

export type Word = {
  token: string
  clean: string
}

export type Definition = {
  definition: string
  ie: string
  lemma: string
  more: string
  partOfSpeech: string
  word: string
  words: string[]
}

type DocumentIndexEntry = {
  chapterNum: number
  numCharacters: number
  title?: string
}

export type Document = {
  id: string
  title: string
  numChapters: number
  index: DocumentIndexEntry[]
}

type Chapter = {
  body: string
  numPages?: number
}

const NUM_CHARACTERS_PER_PAGE = 425

const calculateCharacterNum = (
  doc: Document,
  pageNum: number,
  numCharactersPerPage: number,
): number => {
  if (!doc?.index?.length) return 1

  const [characterNum]: [number, number] = doc.index.reduce(
    ([charNum, num], indexEntry) => {
      if (num >= pageNum) return [charNum, num]

      const numPages = Math.ceil(
        indexEntry.numCharacters / numCharactersPerPage,
      )

      const numPagesForCharacters = Math.min(
        numPages,
        pageNum - num,
      )

      return [
        charNum +
          Math.min(
            numPagesForCharacters * numCharactersPerPage,
            indexEntry.numCharacters,
          ),
        num + numPagesForCharacters,
      ]
    },
    [1, 1],
  )

  return characterNum
}

const calculatePageNum = (
  doc: Document,
  characterNum: number,
  numCharactersPerPage: number,
): number => {
  if (!doc?.index?.length) return 1

  const [, nextPageNum]: [number, number] =
    doc.index.reduce(
      ([num, pageNum], indexEntry) => {
        if (num > characterNum) return [num, pageNum]

        const numCharactersForPages = Math.min(
          indexEntry.numCharacters,
          characterNum - num,
        )

        return [
          num + numCharactersForPages,
          pageNum +
            Math.ceil(
              numCharactersForPages / numCharactersPerPage,
            ),
        ]
      },
      [1, 1],
    )

  return nextPageNum
}

const calculateChapterNum = (
  doc: Document,
  pageNum: number,
  numCharactersPerPage: number,
): number => {
  if (!doc?.index?.length) return 1

  const [, nextChapterNum]: [number, number] =
    doc.index.reduce(
      ([num, chapterNum], indexEntry) => {
        if (num > pageNum) return [num, chapterNum]

        return [
          num +
            Math.ceil(
              indexEntry.numCharacters /
                numCharactersPerPage,
            ),
          chapterNum + 1,
        ]
      },
      [1, 1],
    )

  return nextChapterNum - 1
}

const calculateChapterPageNumStart = (
  doc: Document,
  chapterNum: number,
  numCharactersPerPage: number,
): number => {
  if (!doc?.index?.length) return 0

  const numPagesInPriorChapters: number = doc.index.reduce(
    (num, indexEntry) => {
      if (+indexEntry.chapterNum >= chapterNum) return num

      return (
        num +
        Math.ceil(
          indexEntry.numCharacters / numCharactersPerPage,
        )
      )
    },
    0,
  )

  return numPagesInPriorChapters + 1
}

const useDoc = ({
  documentId,
  fromLang,
  toLang,
  characterNum,
  numCharactersPerPage = NUM_CHARACTERS_PER_PAGE,
}: {
  documentId: string
  fromLang: string
  toLang: string
  characterNum: number
  numCharactersPerPage?: number
}) => {
  const [pageNum, setPageNum] = useState<number>()
  const [chapterNum, setChapterNum] = useState<number>()

  const { data: doc, isLoading: isDocLoading } =
    useQuery<Document>(
      ['doc', documentId, fromLang, toLang],
      () =>
        get(`docs/${documentId}/overview`, {
          params: { fromLang, toLang },
        }),
    )

  const { data, isLoading: isChapterLoading } = useQuery<{
    chapter: Chapter
    translatedChapter: Chapter
  }>(
    ['chapter', documentId, chapterNum, fromLang, toLang],
    () =>
      get(
        `docs/${documentId}/chapters/${chapterNum ?? 1}`,
        { params: { fromLang, toLang } },
      ).then(
        ({
          original: chapterBody,
          translated: translatedChapterBody,
        }) => {
          const numPages = Math.ceil(
            chapterBody.length / NUM_CHARACTERS_PER_PAGE,
          )

          return {
            chapter: {
              body: chapterBody,
              numPages,
            },
            translatedChapter: {
              body: translatedChapterBody,
            },
          }
        },
      ),
  )

  const { chapter, translatedChapter } = data || {}

  const chapterPageNumStart = useMemo(() => {
    if (!doc || chapterNum === undefined) return 1

    return calculateChapterPageNumStart(
      doc,
      chapterNum,
      numCharactersPerPage,
    )
  }, [doc, chapterNum, numCharactersPerPage])

  const chapterNumPages = useMemo(() => {
    if (!doc?.index?.length) return 1

    const chapterIndexEntry = doc.index.find(
      ({ chapterNum: cn }) => cn === chapterNum,
    )

    if (!chapterIndexEntry) return 1

    return Math.ceil(
      chapterIndexEntry.numCharacters /
        numCharactersPerPage,
    )
  }, [chapterNum, doc, numCharactersPerPage])

  const maxNumPages = useMemo(() => {
    if (!doc?.index?.length) return 1
    return doc.index.reduce(
      (np, indexEntry) =>
        np +
        Math.ceil(
          indexEntry.numCharacters / numCharactersPerPage,
        ),
      0,
    )
  }, [doc, numCharactersPerPage])

  const handleSetPageNum = useCallback(
    (newPageNum: number) => {
      if (newPageNum <= 0) return

      if (newPageNum > maxNumPages) {
        setPageNum(maxNumPages)
        return
      }

      setPageNum(newPageNum)
    },
    [maxNumPages],
  )

  /**
   * @return New character number
   */
  const onPageNumChange: (
    delta: number,
  ) => number | undefined = useCallback(
    (delta: number) => {
      if (!chapter || !doc) return

      const newPageNum = (pageNum ?? 0) + delta
      handleSetPageNum(newPageNum)

      return calculateCharacterNum(
        doc,
        newPageNum,
        numCharactersPerPage,
      )
    },
    [
      chapter,
      doc,
      pageNum,
      handleSetPageNum,
      numCharactersPerPage,
    ],
  )

  const onChapterNumChange = useCallback(
    (chapterNum: number) => {
      if (!doc?.index?.length) return

      let newCharacterNum = 0
      for (const indexEntry of doc.index) {
        if (indexEntry.chapterNum === chapterNum) {
          const newPageNum = calculatePageNum(
            doc,
            newCharacterNum,
            numCharactersPerPage,
          )

          if (!newPageNum) {
            return
          }

          handleSetPageNum(newPageNum)
          return
        }

        newCharacterNum += indexEntry.numCharacters
      }
    },
    [doc, handleSetPageNum, numCharactersPerPage],
  )

  useEffect(() => {
    // Can use existing chapter
    if (
      !doc ||
      pageNum === undefined ||
      (pageNum >= chapterPageNumStart &&
        pageNum < chapterPageNumStart + chapterNumPages)
    ) {
      return
    }

    const newChapterNum = calculateChapterNum(
      doc,
      pageNum,
      numCharactersPerPage,
    )

    if (newChapterNum <= 0) return

    setChapterNum(newChapterNum)
  }, [
    chapterNum,
    chapterNumPages,
    chapterPageNumStart,
    doc,
    numCharactersPerPage,
    pageNum,
  ])

  const pages: Record<number, Word[]> | undefined =
    useMemo(() => {
      if (!chapter?.body) return

      const cleanedWords: Word[] = chapter.body
        .split(/\s+/)
        .map((s) => s.trim())
        .filter((s) => s)
        .map((s) => ({ token: s, clean: clean(s) }))

      const result = cleanedWords.reduce(
        (
          {
            mappedPages,
            currentPageNum,
            currentPageCharacterNum,
          }: {
            mappedPages: Record<number, Word[]>
            currentPageNum: number
            currentPageCharacterNum: number
          },
          word: Word,
        ) => {
          const wordLength = word.clean.length + 1

          if (
            currentPageCharacterNum + wordLength >
            numCharactersPerPage
          ) {
            currentPageNum += 1
            currentPageCharacterNum = 0
          }

          if (!mappedPages[currentPageNum]) {
            mappedPages[currentPageNum] = []
          }

          mappedPages[currentPageNum].push(word)
          currentPageCharacterNum += wordLength

          return {
            mappedPages,
            currentPageNum,
            currentPageCharacterNum,
          }
        },
        {
          mappedPages: {},
          currentPageCharacterNum: 0,
          currentPageNum: chapterPageNumStart,
        },
      )

      return result.mappedPages
    }, [
      chapter?.body,
      chapterPageNumStart,
      numCharactersPerPage,
    ])

  const words = useMemo(() => {
    if (!pages || pageNum === null || pageNum === undefined)
      return []
    return pages[pageNum]
  }, [pages, pageNum])

  const translation: string | undefined = useMemo(() => {
    if (!translatedChapter) return

    return translatedChapter.body
  }, [translatedChapter])

  useEffect(() => {
    if (!doc) return

    handleSetPageNum(
      calculatePageNum(
        doc,
        characterNum,
        numCharactersPerPage,
      ),
    )
  }, [
    doc,
    handleSetPageNum,
    numCharactersPerPage,
    characterNum,
  ])

  const currentCharacterNum: undefined | number =
    useMemo(() => {
      if (!doc || pageNum === undefined) return

      return calculateCharacterNum(
        doc,
        pageNum,
        numCharactersPerPage,
      )
    }, [doc, numCharactersPerPage, pageNum])

  return {
    doc,
    pageNum,
    onPageNumChange,
    words,
    translation,
    isLoading: isChapterLoading || isDocLoading,
    characterNum: currentCharacterNum,
    onChapterNumChange,
  }
}

export default useDoc
